import { useEffect, useRef } from 'react';

/**
 * PLAT-11051 — fetches the Section frame previews in the background, before
 * anything asks to display them.
 *
 * The clip preview keeps every tab mounted and hides the inactive ones with
 * `display: none`. A hidden subtree is never "near the viewport", so a
 * `loading="lazy"` image in it is deferred indefinitely — the thumbnails only
 * start loading when the creator opens the Reel Editor, and then arrive one at
 * a time. Warming them from a detached `Image` sidesteps that entirely: the
 * fetch is driven by the loader rather than by layout, so it runs while the tab
 * is still hidden and the rows paint from cache the moment it is shown.
 *
 * Each URL is fetched once per mount. A reorder re-orders the list but not the
 * frames in it, so the already-seen set keeps it from re-requesting them.
 */
export function usePreloadThumbnails(urls: (string | undefined)[]): void {
  const requested = useRef(new Set<string>());
  // A primitive dependency: the array is rebuilt every render, and depending on
  // its identity would re-run this on every pointer move during a drag.
  const key = urls.filter((url): url is string => !!url).join('\n');

  useEffect(() => {
    if (!key) return;

    for (const url of key.split('\n')) {
      if (requested.current.has(url)) continue;
      requested.current.add(url);

      // Deliberately not retained and deliberately not cancelled on unmount:
      // the point is the browser's HTTP cache, which an in-flight fetch still
      // fills after the panel goes away. Aborting would only mean fetching the
      // same frame again next time.
      const image = new Image();
      image.decoding = 'async';
      image.src = url;
    }
  }, [key]);
}
